import { useMemo, useState, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Avatar,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  IconButton,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useAppSelector, useAppDispatch } from 'app/store';
import { selectFeatureFlags } from 'app/store/featureFlagsSlice';
import {
  getUserProfile,
  selectUserProfile,
  updateUserProfile,
  setUserPicture,
} from 'app/store/userProfileSlice';
import { usePhoneChange } from 'app/auth/usePhoneChange';
import AddressCorrectionDialog from 'util/addressValidation/AddressCorrectionDialog';
import { formatComponentMessage } from 'util/addressValidation/formatComponentMessage';
import { containsSecondaryDetails } from 'util/addressValidation/line1Validation';
import { useAddressComponentLabels } from 'util/addressValidation/useAddressComponentLabels';
import { useAddressValidation } from 'util/addressValidation/useAddressValidation';
import MobileLanguageSwitcher from 'app/shared-components/ui/LanguageSwitcher';
import { Auth } from 'aws-amplify';
import { ReactComponent as CameraIcon } from 'assets/camera-icon.svg';
import { ReactComponent as CheckboxIcon } from 'assets/Checkbox.svg';
import MobileTextField from './shared/MobileTextField';
import MobileActionButtons from './shared/MobileActionButtons';
import MobileTypography from './shared/MobileTypography';
import { MobileSpacing } from './shared/MobileResponsiveSpacing';

import { PasswordVisibilityToggle, SectionHeader } from './shared/MobileSettingsComponents';
import { useMobileSettingsNavigation } from './shared/hooks/useMobileSettingsNavigation';
import { BACKGROUND_SIZES_v2 } from 'app/pages/mobile/utils/constants';
import MobilePageContent from 'app/pages/mobile/mobile-layout/MobilePageContent';
import { useMobileResourcePicker } from 'app/hooks/useMobileResourcePicker';
import { getFileExtensionFromDataUrl } from 'app/util/dataUrlUtils';
import { replayChrome, sentryMask } from 'sentryReplayPrivacy';

const PO_BOX_REGEX = /p\.?\s*o\.?\s*box/i;

/**
 * MobileAccountSettings - Profile/Account settings page for mobile app
 * Shows user profile information with routing-based navigation
 */

function MobileAccountSettings(): JSX.Element {
  const { t } = useTranslation();
  const ADDRESS_COMPONENT_LABELS = useAddressComponentLabels();
  const { enqueueSnackbar } = useSnackbar();
  const { handleBackSettings } = useMobileSettingsNavigation();
  const dispatch = useAppDispatch();
  const { profile, picture } = useAppSelector(selectUserProfile);
  const { openCamera, imgPreview } = useMobileResourcePicker(
    profile?.id ? `user-profile-${profile.id}` : undefined
  );

  const DEFAULT_AVATAR = 'assets/images/avatars/profile.jpg';
  const userPicture = picture ?? DEFAULT_AVATAR;

  useEffect(() => {
    if (!imgPreview || !profile?.id) return;

    const uploadPhoto = async () => {
      try {
        // SEE-1319: the stored key is chosen server-side under the signed-in user's id.
        const fileName = `profile.${getFileExtensionFromDataUrl(imgPreview)}`;
        const file = await (await fetch(imgPreview)).blob();

        await dispatch(
          setUserPicture({
            id: profile.id,
            fileName,
            file: new File([file], fileName, { type: file.type }),
          })
        ).unwrap();
      } catch (error) {
        console.error('Failed to upload profile picture:', error);
        enqueueSnackbar(t('profilePictureUploadError'), { variant: 'error' });
      }
    };

    uploadPhoto();
  }, [imgPreview, profile?.id, dispatch, enqueueSnackbar, t]);

  // State for editing address
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const [addressFields, setAddressFields] = useState({
    streetAddress: '',
    extendedAddress: '',
    locality: '',
    region: '',
    postalCode: '',
  });
  const [streetAddressError, setStreetAddressError] = useState<string>('');
  const [extendedAddressError, setExtendedAddressError] = useState<string>('');

  const featureFlags = useAppSelector(selectFeatureFlags);
  const isAddressValidationEnabled = featureFlags.flags?.['enableAddressValidation']?.enabled === true;

  const addressForValidation = useMemo(
    () => ({
      streetAddress: addressFields.streetAddress.trim(),
      extendedAddress: addressFields.extendedAddress.trim(),
      postalCode: addressFields.postalCode.trim(),
      locality: addressFields.locality.trim(),
      region: addressFields.region.trim(),
      countryCodeAlpha3: 'USA',
    }),
    [addressFields]
  );
  const validation = useAddressValidation(addressForValidation);
  const [correctionDeclined, setCorrectionDeclined] = useState(false);

  useEffect(() => {
    if (validation.status.kind !== 'with_changes') {
      setCorrectionDeclined(false);
    }
  }, [validation.status.kind]);

  const triggerValidate = () => {
    if (isAddressValidationEnabled) validation.validate();
  };

  // State for editing phone number
  const [isEditingPhone, setIsEditingPhone] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneExtension, setPhoneExtension] = useState('');
  const [hasExtension, setHasExtension] = useState(false);

  // State for editing password
  const [isEditingPassword, setIsEditingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Format phone number with dashes (e.g., 123-456-7890)
  const formatPhoneNumber = useCallback(
    (phone: string | null | undefined): string => {
      if (!phone) return t('notProvided');

      // Remove all non-digit characters
      const cleaned = phone.replace(/\D/g, '');

      // Check if we have a valid US phone number (10 digits)
      if (cleaned.length === 10) {
        return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
      }

      // Check if we have 11 digits (with country code)
      if (cleaned.length === 11 && cleaned[0] === '1') {
        const withoutCountryCode = cleaned.slice(1);
        return `${withoutCountryCode.slice(0, 3)}-${withoutCountryCode.slice(
          3,
          6
        )}-${withoutCountryCode.slice(6)}`;
      }

      // Return original if not a standard format
      return phone;
    },
    [t]
  );

  // Edit handlers
  const handleEditAddress = () => {
    setStreetAddressError('');
    setExtendedAddressError('');
    // Parse current address to populate fields
    if (profile?.address) {
      try {
        const rawAddress =
          typeof profile.address === 'string' ? profile.address : JSON.stringify(profile.address);
        const address = JSON.parse(rawAddress.replace(/\\/g, ''));
        setAddressFields({
          streetAddress: address.streetAddress || '',
          extendedAddress: address.extendedAddress || '',
          locality: address.locality || '',
          region: address.region || '',
          postalCode: address.postalCode || '',
        });
      } catch {
        // If parsing fails, start with empty fields
        setAddressFields({
          streetAddress: '',
          extendedAddress: '',
          locality: '',
          region: '',
          postalCode: '',
        });
      }
    }
    setIsEditingAddress(true);
  };

  const proceedSaveAddress = async (fields: typeof addressFields) => {
    if (!profile?.id) return;

    try {
      await dispatch(
        updateUserProfile({
          id: profile.id,
          address: JSON.stringify({
            streetAddress: fields.streetAddress.trim(),
            extendedAddress: fields.extendedAddress.trim(),
            postalCode: fields.postalCode.trim(),
            locality: fields.locality.trim(),
            region: fields.region.trim(),
          }),
        })
      ).unwrap();
      setIsEditingAddress(false);
    } catch (error) {
      // Failed to update address - could add a toast notification here
      setIsEditingAddress(false);
    }
  };

  const handleSaveAddress = async () => {
    if (!profile?.id) return;

    let blocked = false;
    if (PO_BOX_REGEX.test(addressFields.streetAddress)) {
      setStreetAddressError(t('poBoxNotAllowed'));
      blocked = true;
    }
    if (PO_BOX_REGEX.test(addressFields.extendedAddress)) {
      setExtendedAddressError(t('poBoxNotAllowed'));
      blocked = true;
    }
    if (blocked) return;

    if (isAddressValidationEnabled) {
      const final = await validation.validateOnSubmit();
      if (final.kind === 'with_changes') {
        setCorrectionDeclined(false);
        return;
      }
      if (final.kind === 'incomplete' || final.kind === 'invalid' || final.kind === 'transport_error') {
        return;
      }
    }

    proceedSaveAddress(addressFields);
  };

  const applyAddressCorrection = () => {
    if (validation.status.kind !== 'with_changes') return;
    const { corrected } = validation.status;
    setAddressFields({
      streetAddress: corrected.streetAddress,
      extendedAddress: corrected.extendedAddress ?? '',
      postalCode: corrected.postalCode,
      locality: corrected.locality,
      region: corrected.region,
    });
    validation.resolveCorrections('accept');
  };

  const handleAddressCorrectionDecision = (decision: 'accept' | 'decline') => {
    if (validation.status.kind !== 'with_changes') return;
    if (decision === 'accept') {
      applyAddressCorrection();
    } else {
      setCorrectionDeclined(true);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingAddress(false);
    setStreetAddressError('');
    setExtendedAddressError('');
  };

  // SEE-221: the phone number is a security factor and can't be edited inline.
  // "Edit" opens the verified change flow (email-authorize → new-number SMS).
  const { start: startPhoneChange, dialog: phoneChangeDialog } = usePhoneChange({
    onChanged: () => {
      if (profile?.id) dispatch(getUserProfile(profile.id));
    },
  });

  const handleEditPhone = () => startPhoneChange();

  const handleSavePhone = async () => {
    if (!profile?.id) return;
    try {
      // phone_number is read-only via the API now (changed via the wizard); this
      // inline save only persists the extension.
      await dispatch(
        updateUserProfile({
          id: profile.id,
          phone_extension: hasExtension ? phoneExtension.trim() : '',
        })
      ).unwrap();
      setIsEditingPhone(false);
    } catch (error) {
      setIsEditingPhone(false);
    }
  };

  const handleCancelPhoneEdit = () => {
    setIsEditingPhone(false);
    setPhoneNumber('');
    setPhoneExtension('');
    setHasExtension(false);
  };

  const handleEditPassword = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setIsEditingPassword(true);
  };

  const handleSavePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) return;
    if (newPassword !== confirmPassword) return;

    try {
      const user = await Auth.currentAuthenticatedUser();
      await Auth.changePassword(user, currentPassword, newPassword);
      setIsEditingPassword(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      enqueueSnackbar(t('mobilePasswordChangeSuccess'), { variant: 'success' });
    } catch (error: unknown) {
      // Handle specific error cases
      let errorMessage = t('mobilePasswordChangeError');

      // Type guard to check if error has a code property
      if (error && typeof error === 'object' && 'code' in error) {
        const errorWithCode = error as { code: string };

        if (errorWithCode.code === 'NotAuthorizedException') {
          errorMessage = t('mobilePasswordChangeErrorWrongCurrent');
        } else if (errorWithCode.code === 'InvalidPasswordException') {
          errorMessage = t('mobilePasswordChangeErrorInvalidNew');
        } else if (errorWithCode.code === 'LimitExceededException') {
          errorMessage = t('mobilePasswordChangeErrorLimitExceeded');
        }
      }

      enqueueSnackbar(errorMessage, { variant: 'error' });
      // Don't close the form on error so user can try again
    }
  };

  const handleCancelPasswordEdit = () => {
    setIsEditingPassword(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  // Password validation
  const hasMinLength = newPassword.length >= 8;
  const hasCapitalLetter = /[A-Z]/.test(newPassword);
  const hasNumber = /\d/.test(newPassword);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(newPassword);
  const passwordsMatch = newPassword === confirmPassword && newPassword !== '';
  const isPasswordFormValid =
    currentPassword.trim() !== '' &&
    hasMinLength &&
    hasCapitalLetter &&
    hasNumber &&
    hasSpecialChar &&
    passwordsMatch;

  // Parse user address - same logic as MobileCardSidesheet
  const userAddress = useMemo(() => {
    if (!profile?.address) return t('notProvided');

    try {
      const rawAddress =
        typeof profile.address === 'string' ? profile.address : JSON.stringify(profile.address);
      const address = JSON.parse(rawAddress.replace(/\\/g, ''));
      const parts = [
        address.streetAddress,
        address.extendedAddress,
        address.locality,
        address.region,
        address.postalCode ? `, ${address.postalCode}` : '',
      ].filter(Boolean);

      return parts.join(' ').replace(' ,', ',');
    } catch {
      return t('notProvided');
    }
  }, [profile?.address, t]);

  return (
    <MobilePageContent
      transparentHeight={BACKGROUND_SIZES_v2.SHOW_HEADER}
      title={t('mobileProfileTitle')}
      onBack={handleBackSettings}
    >
      {/* Top Row: Profile Circle and Language Toggle */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mb: MobileSpacing.marginBottomLarge,
        }}
      >
        {/* Avatar with camera icon overlay */}
        <Box
          sx={{
            position: 'relative',
            display: 'inline-block',
          }}
        >
          <Avatar
            {...sentryMask}
            src={imgPreview ?? userPicture}
            sx={{
              width: 80,
              height: 80,
              mt: MobileSpacing.gapSmall,
              bgcolor: 'primary.main',
            }}
          >
            {profile?.first_name?.charAt(0) || 'U'}
          </Avatar>
          {/* Camera icon overlay - visual only */}
          <IconButton
            sx={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              width: 32,
              height: 32,
              bgcolor: 'transparent',
              border: 'none',
              borderRadius: '50%',
              '&:hover': {
                transform: profile?.id ? 'scale(1.05)' : 'none',
              },
              '&:active': {
                transform: profile?.id ? 'scale(0.95)' : 'none',
              },
              opacity: profile?.id ? 1 : 0.5,
              cursor: profile?.id ? 'pointer' : 'not-allowed',
              p: 0,
            }}
            aria-label="Camera icon (display only)"
            onClick={() => profile?.id && openCamera()}
            disabled={!profile?.id}
          >
            <CameraIcon width="32" height="32" />
          </IconButton>
        </Box>
        <Box sx={{ transform: 'scaleY(0.9)' }}>
          <MobileLanguageSwitcher />
        </Box>
      </Box>

      {/* Profile Information Fields. Replay mask for the whole profile; buttons, field labels and
          helper text stay visible, and the plain-text chrome below carries replayChrome. */}
      <Box
        {...sentryMask}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: MobileSpacing.gapLarge,
          width: '100%',
          maxWidth: '100%',
          pb: MobileSpacing.marginBottomXLarge, // Extra bottom padding for scroll space when editing
        }}
      >
        {/* Name */}
        <Box>
          <SectionHeader
            label={t('mobileProfileName')}
            onEdit={() => undefined} // Name is not editable
            isEditing={false}
            editAriaLabel=""
            isEditable={false}
          />
          <MobileTypography variant="body">
            {profile?.first_name && profile?.last_name
              ? `${profile.first_name} ${profile.last_name}`
              : t('notProvided')}
          </MobileTypography>
        </Box>

        {/* Email */}
        <Box>
          <SectionHeader
            label={t('mobileProfileEmail')}
            onEdit={() => undefined} // Email is not editable
            isEditing={false}
            editAriaLabel=""
            isEditable={false}
          />
          <MobileTypography variant="body">{profile?.email || t('notProvided')}</MobileTypography>
        </Box>

        {/* Mailing Address */}
        <Box>
          <SectionHeader
            label={t('mobileProfileMailingAddress')}
            onEdit={handleEditAddress}
            isEditing={isEditingAddress}
            editAriaLabel="Edit address"
          />

          {isEditingAddress ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <MobileTextField
                label={t('streetAddress')}
                value={addressFields.streetAddress}
                onChange={(e) => {
                  setAddressFields({ ...addressFields, streetAddress: e.target.value });
                  if (streetAddressError) setStreetAddressError('');
                }}
                onBlur={() => {
                  if (PO_BOX_REGEX.test(addressFields.streetAddress)) {
                    setStreetAddressError(t('poBoxNotAllowed'));
                  } else {
                    triggerValidate();
                  }
                }}
                error={!!streetAddressError}
                helperText={streetAddressError}
                fullWidth
                variant="outlined"
              />
              {containsSecondaryDetails(addressFields.streetAddress) && (
                <Typography {...replayChrome} variant="body2" color="warning.main" sx={{ mt: -1 }}>
                  {t('addressLine1HasSecondaryDetails')}
                </Typography>
              )}
              <MobileTextField
                label={t('apartmentSuiteEtc')}
                value={addressFields.extendedAddress}
                onChange={(e) => {
                  setAddressFields({ ...addressFields, extendedAddress: e.target.value });
                  if (extendedAddressError) setExtendedAddressError('');
                }}
                onBlur={() => {
                  if (PO_BOX_REGEX.test(addressFields.extendedAddress)) {
                    setExtendedAddressError(t('poBoxNotAllowed'));
                  } else {
                    triggerValidate();
                  }
                }}
                error={!!extendedAddressError}
                helperText={extendedAddressError}
                fullWidth
                variant="outlined"
              />
              <MobileTextField
                label={t('mobileProfileCity')}
                value={addressFields.locality}
                onChange={(e) => setAddressFields({ ...addressFields, locality: e.target.value })}
                onBlur={triggerValidate}
                fullWidth
                variant="outlined"
              />
              <Box sx={{ display: 'flex', gap: 2 }}>
                <MobileTextField
                  label={t('mobileProfileState')}
                  value={addressFields.region}
                  onChange={(e) => setAddressFields({ ...addressFields, region: e.target.value })}
                  onBlur={triggerValidate}
                  sx={{ flex: 1 }}
                />
                <MobileTextField
                  label={t('mobileProfileZipCode')}
                  value={addressFields.postalCode}
                  onChange={(e) => setAddressFields({ ...addressFields, postalCode: e.target.value })}
                  onBlur={triggerValidate}
                  sx={{ flex: 1 }}
                />
              </Box>

              {isAddressValidationEnabled &&
                (validation.status.kind === 'incomplete' || validation.status.kind === 'invalid') && (
                  <Typography {...replayChrome} variant="body1" color="error" sx={{ fontWeight: 500 }}>
                    {formatComponentMessage(validation.status, ADDRESS_COMPONENT_LABELS)}
                  </Typography>
                )}
              {isAddressValidationEnabled && validation.status.kind === 'transport_error' && (
                <Box>
                  <Typography {...replayChrome} variant="body1" color="error" sx={{ fontWeight: 500 }}>
                    {t('addressValidationTransportError')}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    sx={{ mt: 1 }}
                    onClick={() => validation.validate()}
                  >
                    {t('retry')}
                  </Button>
                </Box>
              )}
              {isAddressValidationEnabled &&
                validation.status.kind === 'with_changes' &&
                correctionDeclined && (
                  <Box>
                    <Typography {...replayChrome} variant="body1" color="error" sx={{ fontWeight: 500 }}>
                      {t('addressNotVerified')}
                    </Typography>
                    <Button size="small" variant="outlined" sx={{ mt: 1 }} onClick={applyAddressCorrection}>
                      {t('useSuggestedAddress')}
                    </Button>
                  </Box>
                )}

              <MobileActionButtons onCancel={handleCancelEdit} onSave={handleSaveAddress} />
            </Box>
          ) : (
            <MobileTypography variant="body">{userAddress}</MobileTypography>
          )}

          {validation.status.kind === 'with_changes' && !correctionDeclined && (
            <AddressCorrectionDialog
              open
              original={validation.status.original}
              corrected={validation.status.corrected}
              onResolve={handleAddressCorrectionDecision}
            />
          )}
        </Box>

        {/* Phone Number */}
        <Box>
          <SectionHeader
            label={t('mobileProfilePhoneNumber')}
            onEdit={handleEditPhone}
            isEditing={isEditingPhone}
            editAriaLabel="Edit phone number"
          />

          {isEditingPhone ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <MobileTextField
                label={t('mobileProfilePhoneNumber')}
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                fullWidth
                variant="outlined"
                placeholder="+1 (555) 123-4567"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={hasExtension}
                    onChange={(e) => setHasExtension(e.target.checked)}
                    sx={{
                      color: '#E0E0E0',
                      '&.Mui-checked': {
                        color: '#324C80',
                      },
                    }}
                  />
                }
                label={
                  <Typography
                    {...replayChrome}
                    sx={{
                      color: '#212121',
                      fontFamily:
                        '"SF Pro", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      fontSize: '16px',
                      fontWeight: 274,
                      lineHeight: '24px',
                    }}
                  >
                    {t('mobileProfileHasExtension')}
                  </Typography>
                }
              />
              {hasExtension && (
                <MobileTextField
                  label={t('mobileProfileExtension')}
                  value={phoneExtension}
                  onChange={(e) => setPhoneExtension(e.target.value)}
                  fullWidth
                  variant="outlined"
                />
              )}
              <MobileActionButtons onCancel={handleCancelPhoneEdit} onSave={handleSavePhone} />
            </Box>
          ) : (
            <MobileTypography variant="body">{formatPhoneNumber(profile?.phone_number)}</MobileTypography>
          )}
        </Box>

        {/* Password */}
        <Box>
          <SectionHeader
            label={t('mobileProfilePassword')}
            onEdit={handleEditPassword}
            isEditing={isEditingPassword}
            editAriaLabel="Change password"
          />

          {isEditingPassword ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <MobileTextField
                label={t('mobileProfileCurrentPassword')}
                type={showCurrentPassword ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                fullWidth
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <PasswordVisibilityToggle
                        showPassword={showCurrentPassword}
                        onToggle={() => setShowCurrentPassword(!showCurrentPassword)}
                      />
                    </InputAdornment>
                  ),
                }}
              />
              <MobileTextField
                label={t('mobileProfileNewPassword')}
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                fullWidth
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <PasswordVisibilityToggle
                        showPassword={showNewPassword}
                        onToggle={() => setShowNewPassword(!showNewPassword)}
                      />
                    </InputAdornment>
                  ),
                }}
              />
              <MobileTextField
                label={t('mobileProfileConfirmNewPassword')}
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                fullWidth
                variant="outlined"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <PasswordVisibilityToggle
                        showPassword={showConfirmPassword}
                        onToggle={() => setShowConfirmPassword(!showConfirmPassword)}
                      />
                    </InputAdornment>
                  ),
                }}
              />
              {newPassword && (
                <Box sx={{ mt: MobileSpacing.gapLarge }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    {hasMinLength ? (
                      <CheckboxIcon
                        style={{
                          width: 20,
                          height: 20,
                          marginRight: 8,
                        }}
                      />
                    ) : (
                      <Typography
                        sx={{
                          color: 'text.primary',
                          fontSize: 14,
                          fontWeight: 400,
                          mr: 1,
                        }}
                      >
                        •
                      </Typography>
                    )}
                    <Typography
                      {...replayChrome}
                      sx={{
                        color: 'text.primary',
                        fontSize: 14,
                        fontWeight: 400,
                        lineHeight: '20px',
                      }}
                    >
                      {t('mobileProfilePasswordRequirements.minimumLength')}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    {hasCapitalLetter ? (
                      <CheckboxIcon
                        style={{
                          width: 20,
                          height: 20,
                          marginRight: 8,
                        }}
                      />
                    ) : (
                      <Typography
                        sx={{
                          color: 'text.primary',
                          fontSize: 14,
                          fontWeight: 400,
                          mr: 1,
                        }}
                      >
                        •
                      </Typography>
                    )}
                    <Typography
                      {...replayChrome}
                      sx={{
                        color: 'text.primary',
                        fontSize: 14,
                        fontWeight: 400,
                        lineHeight: '20px',
                      }}
                    >
                      {t('mobileProfilePasswordRequirements.capitalLetter')}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    {hasNumber ? (
                      <CheckboxIcon
                        style={{
                          width: 20,
                          height: 20,
                          marginRight: 8,
                        }}
                      />
                    ) : (
                      <Typography
                        sx={{
                          color: 'text.primary',
                          fontSize: 14,
                          fontWeight: 400,
                          mr: 1,
                        }}
                      >
                        •
                      </Typography>
                    )}
                    <Typography
                      {...replayChrome}
                      sx={{
                        color: 'text.primary',
                        fontSize: 14,
                        fontWeight: 400,
                        lineHeight: '20px',
                      }}
                    >
                      {t('mobileProfilePasswordRequirements.numericalValue')}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    {hasSpecialChar ? (
                      <CheckboxIcon
                        style={{
                          width: 20,
                          height: 20,
                          marginRight: 8,
                        }}
                      />
                    ) : (
                      <Typography
                        sx={{
                          color: 'text.primary',
                          fontSize: 14,
                          fontWeight: 400,
                          mr: 1,
                        }}
                      >
                        •
                      </Typography>
                    )}
                    <Typography
                      {...replayChrome}
                      sx={{
                        color: 'text.primary',
                        fontSize: 14,
                        fontWeight: 400,
                        lineHeight: '20px',
                      }}
                    >
                      {t('mobileProfilePasswordRequirements.specialCharacter')}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    {passwordsMatch ? (
                      <CheckboxIcon
                        style={{
                          width: 20,
                          height: 20,
                          marginRight: 8,
                        }}
                      />
                    ) : (
                      <Typography
                        sx={{
                          color: 'text.primary',
                          fontSize: 14,
                          fontWeight: 400,
                          mr: 1,
                        }}
                      >
                        •
                      </Typography>
                    )}
                    <Typography
                      {...replayChrome}
                      sx={{
                        color: 'text.primary',
                        fontSize: 14,
                        fontWeight: 400,
                        lineHeight: '20px',
                      }}
                    >
                      {t('mobileProfilePasswordRequirements.passwordsMatch')}
                    </Typography>
                  </Box>
                </Box>
              )}
              <MobileActionButtons
                onCancel={handleCancelPasswordEdit}
                onSave={handleSavePassword}
                isSaveDisabled={!isPasswordFormValid}
              />
            </Box>
          ) : (
            <MobileTypography variant="body">********</MobileTypography>
          )}
        </Box>
      </Box>
      {phoneChangeDialog}
    </MobilePageContent>
  );
}

export default MobileAccountSettings;
